import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setPassword, getCurrentAdmin } from '../../services/adminAuthService.js';
import AdminPageHeader from '../../components/admin/ui/AdminPageHeader';
import AdminAlert from '../../components/admin/ui/AdminAlert';
import AdminButton from '../../components/admin/ui/AdminButton';
import AdminSpinner from '../../components/admin/ui/AdminSpinner';

/**
 * ResetPasswordPage
 * 
 * Page for users who are forced to reset their password after login.
 * User is already authenticated (session exists), but must_reset_password=true.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const passwordInputRef = useRef(null);

  useEffect(() => {
    // Get current user email on mount
    const loadUser = async () => {
      try {
        const user = await getCurrentAdmin();
        if (!user) {
          // Not authenticated, redirect to login
          navigate('/admin/login');
          return;
        }
        if (!user.mustResetPassword) {
          // Password already reset, redirect to admin home
          navigate('/admin');
          return;
        }
        setEmail(user.email || '');
      } catch (err) {
        setError(err.message || 'Failed to load user information');
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, [navigate]);

  useEffect(() => {
    // Focus password field when ready
    if (!loading && passwordInputRef.current) {
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
    }
  }, [loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!password.trim() || !confirmPassword.trim()) {
      setError('Password and confirmation are required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password.length > 128) {
      setError('Password must be no more than 128 characters');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await setPassword(password);
      // Password set successfully, redirect to admin home
      navigate('/admin');
    } catch (err) {
      setError(err.message || 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-content-panel">
        <AdminPageHeader title="Reset Password" />
        <div className="admin-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--spacing-2)', padding: 'var(--spacing-8)' }}>
          <AdminSpinner size="md" />
          <span style={{ color: 'var(--color-text-tertiary)' }}>Loading...</span>
        </div>
      </div>
    );
  }

  const isValidPassword = password.length >= 12;
  const passwordsMatch = password === confirmPassword;
  const canSubmit = isValidPassword && passwordsMatch && !submitting;

  return (
    <div className="admin-content-panel">
      <AdminPageHeader title="Reset Password" />
      
      <div style={{ maxWidth: '540px', margin: '0 auto', padding: 'var(--spacing-6)' }}>
        <div style={{ marginBottom: 'var(--spacing-6)' }}>
          <AdminAlert type="warning" message="You must reset your password before continuing." dismissible={false} />
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <label className="admin-form-label">
              Email
              <input
                type="email"
                value={email}
                disabled
                style={{
                  width: '100%',
                  padding: 'var(--spacing-3)',
                  border: '1px solid var(--color-border-light)',
                  borderRadius: 'var(--radius-sm)',
                  backgroundColor: 'var(--color-bg-secondary)',
                  color: 'var(--color-text-secondary)',
                  fontSize: 'var(--font-size-sm)',
                }}
                aria-label="Email (read-only)"
              />
            </label>
            <p style={{ marginTop: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
              Your email address (read-only)
            </p>
          </div>

          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <label className="admin-form-label">
              New Password <span style={{ color: 'var(--color-error)' }}>*</span>
              <input
                ref={passwordInputRef}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password (minimum 8 characters)"
                style={{
                  width: '100%',
                  padding: 'var(--spacing-3)',
                  border: `1px solid ${password && !isValidPassword ? 'var(--color-error)' : 'var(--color-border-light)'}`,
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-sm)',
                }}
                disabled={submitting}
                aria-required="true"
                aria-invalid={password && !isValidPassword}
              />
            </label>
            {password && !isValidPassword && (
              <p style={{ marginTop: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-error)' }}>
                Password must be 8–128 characters
              </p>
            )}
            <p style={{ marginTop: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
              Password must be at least 8 characters (max 128).
            </p>
          </div>

          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <label className="admin-form-label">
              Confirm Password <span style={{ color: 'var(--color-error)' }}>*</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                style={{
                  width: '100%',
                  padding: 'var(--spacing-3)',
                  border: `1px solid ${confirmPassword && !passwordsMatch ? 'var(--color-error)' : 'var(--color-border-light)'}`,
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-sm)',
                }}
                disabled={submitting}
                aria-required="true"
                aria-invalid={confirmPassword && !passwordsMatch}
              />
            </label>
            {confirmPassword && !passwordsMatch && (
              <p style={{ marginTop: 'var(--spacing-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-error)' }}>
                Passwords do not match
              </p>
            )}
          </div>

          {error && (
            <div style={{ marginBottom: 'var(--spacing-4)' }}>
              <AdminAlert type="error" message={error} dismissible={false} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 'var(--spacing-3)', justifyContent: 'flex-end' }}>
            <AdminButton
              variant="primary"
              type="submit"
              disabled={!canSubmit || submitting}
              ariaLabel="Set new password"
            >
              {submitting ? 'Setting Password...' : 'Set Password'}
            </AdminButton>
          </div>
        </form>
      </div>
    </div>
  );
}
